import type { DriverDietForm } from '@/modules/activities/components/overview/DriverDietPrompt'
import { DriverDietPrompt } from '@/modules/activities/components/overview/DriverDietPrompt'
import { useDietDayMutations } from '@/modules/activities/mutations.hooks'
import { Module, ModuleOptionsValue } from '@/modules/companies'
import { useModuleConfig } from '@/utils/hooks'
import { usePrompt } from '@ur-solutions/react-components'
import type { WorkPeriodNode } from '../types.graphql'

export type DriverDietFields = NonNullable<WorkPeriodNode['driverDiet']>

interface Anchor {
  workPeriodId?: string | null
  restPeriodId?: string | null
  /** Above 1 for a rest: one per night it covers. */
  maxPartOfDay?: number
  driverDiet?: DriverDietFields | null
}

/**
 * Opening the diet form and writing the answer, without a button around it.
 *
 * The mutation hook reaches for the settings store through its error handler,
 * which the prompt portal renders outside of — so a component that owns the
 * mutation cannot itself be rendered inside a prompt. Keeping the write here
 * lets any caller in the normal tree (a row button, a calendar bar) open the
 * form, which is a plain form and portals fine.
 */
export function useEditDriverDiet(userId: string, onChange?: () => void) {
  const addPrompt = usePrompt()
  const dietMutations = useDietDayMutations()
  const { getModuleOptionStatus } = useModuleConfig(Module.ACTIVITIES)
  const simpleDietViewActive = getModuleOptionStatus(
    ModuleOptionsValue.ACTIVITIES_SIMPLE_DIET_OPTION
  )
  const commentMandatory = getModuleOptionStatus(
    ModuleOptionsValue.ACTIVITIES_DIET_COMMENT_MANDATORY
  )

  return async function editDriverDiet({
    workPeriodId = null,
    restPeriodId = null,
    maxPartOfDay = 1,
    driverDiet,
  }: Anchor) {
    const { data } = await addPrompt<DriverDietForm | null>(resolve => (
      <DriverDietPrompt
        initialData={{
          userId,
          salaryCodeId: driverDiet?.salaryCode?.id ?? null,
          partOfDay: driverDiet?.partOfDay ?? 1,
          comment: driverDiet?.comment ?? '',
        }}
        simpleView={simpleDietViewActive}
        commentMandatory={commentMandatory}
        edit={!!driverDiet}
        maxPartOfDay={maxPartOfDay}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    await dietMutations.updateOrCreateDriverDiet({
      variables: {
        input: { ...data, workPeriod: workPeriodId, restPeriod: restPeriodId },
      },
    })
    onChange?.()
  }
}
